import * as React from "react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Check, Circle, Loader2, AlertCircle, Clock, Pencil } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

interface FeatureDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureId: string | null;
  onEdit: () => void;
  onDelete: () => void;
}

const taskStatusConfig: Record<
  string,
  { label: string; icon: React.ElementType; className: string }
> = {
  todo: {
    label: "To Do",
    icon: Circle,
    className: "text-muted-foreground",
  },
  in_progress: {
    label: "In Progress",
    icon: Clock,
    className: "text-blue-600",
  },
  done: {
    label: "Done",
    icon: Check,
    className: "text-emerald-600",
  },
  blocked: {
    label: "Blocked",
    icon: AlertCircle,
    className: "text-red-600",
  },
};

const nextStatus: Record<string, string> = {
  todo: "in_progress",
  in_progress: "done",
  done: "todo",
  blocked: "todo",
};

const priorityConfig: Record<string, { label: string; className: string }> = {
  low: { label: "Low", className: "bg-muted text-muted-foreground" },
  medium: { label: "Medium", className: "bg-blue-500/10 text-blue-600" },
  high: { label: "High", className: "bg-amber-500/10 text-amber-600" },
  urgent: { label: "Urgent", className: "bg-red-500/10 text-red-600" },
};

export function FeatureDetailModal({
  open,
  onOpenChange,
  featureId,
  onEdit,
  onDelete,
}: FeatureDetailModalProps) {
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const utils = trpc.useUtils();

  const { data: feature } = trpc.feature.getById.useQuery(
    { id: featureId! },
    { enabled: !!featureId },
  );

  const { data: tasks = [] } = trpc.task.listByFeature.useQuery(
    { featureId: featureId! },
    { enabled: !!featureId },
  );

  const createTask = trpc.task.create.useMutation({
    onSuccess: () => {
      utils.task.listByFeature.invalidate({ featureId: featureId! });
      utils.feature.list.invalidate();
      setNewTaskTitle("");
    },
  });

  const updateTask = trpc.task.update.useMutation({
    onSuccess: () => {
      utils.task.listByFeature.invalidate({ featureId: featureId! });
    },
  });

  const deleteTask = trpc.task.delete.useMutation({
    onSuccess: () => {
      utils.task.listByFeature.invalidate({ featureId: featureId! });
      utils.feature.list.invalidate();
    },
  });

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !featureId) return;
    createTask.mutate({ featureId, title: newTaskTitle.trim() });
  };

  const handleToggleStatus = (taskId: string, currentStatus: string) => {
    const next = nextStatus[currentStatus] ?? "todo";
    updateTask.mutate({
      id: taskId,
      status: next as "todo" | "in_progress" | "done" | "blocked",
    });
  };

  if (!feature) return null;

  const pConfig = (priorityConfig[feature.priority] ?? priorityConfig.medium)!;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-1">
              <DialogTitle className="text-lg">{feature.name}</DialogTitle>
              {feature.description && (
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Badge variant="outline" className="text-xs">
              {feature.status.replace("_", " ")}
            </Badge>
            <span
              className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", pConfig.className)}
            >
              {pConfig.label}
            </span>
          </div>
        </DialogHeader>

        <Separator />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
              Tasks ({tasks.length})
            </h4>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onDelete}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Delete
              </Button>
            </div>
          </div>

          <ScrollArea className="max-h-[300px]">
            <div className="space-y-1">
              {tasks.map((task) => {
                const statusConf = (taskStatusConfig[task.status] ?? taskStatusConfig.todo)!;
                const StatusIcon = statusConf.icon;
                return (
                  <div
                    key={task.id}
                    className="group flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                  >
                    <button
                      onClick={() => handleToggleStatus(task.id, task.status)}
                      className={cn("shrink-0 transition-colors", statusConf.className)}
                    >
                      <StatusIcon className="h-4 w-4" />
                    </button>
                    <span
                      className={cn(
                        "flex-1 text-sm",
                        task.status === "done" && "text-muted-foreground line-through",
                      )}
                    >
                      {task.title}
                    </span>
                    <button
                      onClick={() => deleteTask.mutate({ id: task.id })}
                      className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {tasks.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No tasks yet. Add one below.
                </p>
              )}
            </div>
          </ScrollArea>

          <form onSubmit={handleAddTask} className="flex gap-2">
            <Input
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              placeholder="Add a task..."
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={!newTaskTitle.trim() || createTask.isPending}>
              {createTask.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
